import * as React from 'react';
import { Box, FormLabel, lighten, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  DateTimePicker as MuiDateTimePicker,
  DateTimePickerProps as MuiDateTimePickerProps,
} from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from '@askporter/config-provider';
import { IconSize, Icon } from '../Icon';

export interface DateTimePickerProps
  extends Omit<MuiDateTimePickerProps, 'onChange' | 'value' | 'maxDate' | 'minDate' | 'maxTime' | 'minTime'> {
  value: string | null;
  onChange: (value: string) => void;
  label?: string;
  formLabel?: string;
  maxDate?: string | null;
  minDate?: string | null;
  maxTime?: string | null;
  minTime?: string | null;
  id?: string;
}

const StyledDatePicker = styled(MuiDateTimePicker)(({ theme }) => ({
  '.MuiFormControl-root': {
    marginTop: 0,
    marginBottom: 0,
  },
  '.MuiPickersInputBase-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#414651',
    WebkitTextFillColor: '#414651',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
      marginLeft: '6px',
    },
    flexDirection: 'row-reverse',
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      '& .MuiIconButton-root': {
        marginLeft: '-8px',
      },
    },
  },
}));

const OpenPickerIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})({});

const parseHHmm = (t?: string | null) => (t ? dayjs(t, 'HH:mm', true) : null);

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  label,
  formLabel,
  maxDate,
  minDate,
  minTime,
  maxTime,
  ...props
}) => {
  const [val, setVal] = React.useState<Dayjs | null>(value ? dayjs(value) : null);
  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';

  // Sync internal state with external value prop
  React.useEffect(() => {
    setVal(value ? dayjs(value) : null);
  }, [value]);

  // Create referenceDate with minTime so the time picker starts at minTime when opened
  const getReferenceDate = (): Dayjs => {
    const today = dayjs();
    const parsedMinTime = parseHHmm(minTime);

    if (parsedMinTime) {
      return today.hour(parsedMinTime.hour()).minute(parsedMinTime.minute()).second(0).millisecond(0);
    }

    return today;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {formLabel && (
        <FormLabel
          sx={{
            ml: '2px',
            mb: '4px',
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            color: '#344054',
          }}
        >
          {formLabel}
        </FormLabel>
      )}
      <StyledDatePicker
        value={val}
        onChange={(v) => setVal(v)} // update preview only
        onAccept={(v) => onChange(v ? v.format('YYYY-MM-DD HH:mm') : '')} // commit on OK
        disabled={props.disabled}
        format={adapterLocale === 'de' ? 'DD. MMM YYYY HH:mm' : 'DD MMM YYYY HH:mm'}
        slots={{
          openPickerIcon: () => (
            <OpenPickerIcon>
              <Icon size={IconSize.MD} folder="design-system/calendar" />
            </OpenPickerIcon>
          ),
        }}
        slotProps={{
          textField: {
            inputProps: { 'aria-label': label },
            readOnly: props.readOnly,
            sx: { marginTop: 0, marginBottom: 0 },
          },
          openPickerButton: {
            'aria-label': label,
          },
          dialog: { 'aria-label': label },
          popper: { 'aria-label': label },
        }}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        minDate={minDate ? dayjs(minDate) : undefined}
        minTime={parseHHmm(minTime) ?? undefined}
        maxTime={parseHHmm(maxTime) ?? undefined}
        referenceDate={getReferenceDate()}
        id={props.id}
        ampm={false}
        {...props}
      />
    </LocalizationProvider>
  );
};
